import React, { useEffect, useState } from 'react';
import AdminLayout from '../../../layouts/AdminLayout';
import Footer from '../../../shared/components/Footer';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { privateApi } from '../../../api/api';
import { toast } from 'sonner';
import Swal from 'sweetalert2';
import { Dropdown } from 'react-bootstrap';
import Rates from '../components/Rates';
import CreateRoomTypeDialog from '../components/CreateRoomTypeDialog';
import CreateRateDialog from '../components/CreateRateDialog';
import CreateLinkRateDialog from '../components/CreateLinkRateDialog';
import UpdateRoomTypeDIalog from '../components/UpdateRoomTypeDIalog';
import UpdateRoomNumberDialog from '../components/UpdateRoomNumberDialog';
import UpdateRateDialog from '../components/UpdateRateDialog';
import UpdateLinkRateDialog from '../components/UpdateLinkRateDialog';
import RequireAuth from '../../../auth/middleware/RequireAuth';
import { AxiosError } from 'axios';
import { IconSortAscendingLetters, IconSortDescendingLetters, IconGripVertical } from '@tabler/icons-react';
import clsx from 'clsx';
import {
    DndContext,
    closestCenter,
    KeyboardSensor,
    PointerSensor,
    useSensor,
    useSensors,
    DragEndEvent
} from '@dnd-kit/core';
import {
    arrayMove,
    SortableContext,
    sortableKeyboardCoordinates,
    verticalListSortingStrategy,
    useSortable
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useSortRoomTypes } from '../hooks/useSortRoomTypes';
import ReadOnlyGuard from '../../../shared/components/ReadOnlyGuard';
import PropertyInactiveBanner from '../../../shared/components/PropertyInactiveBanner';
import { usePropertyReadOnly } from '../../../shared/hooks/usePropertyReadOnly';

const RoomRatesSettingPage: React.FC = () => {
    const [searchQuery, setSearchQuery] = useState<string>('');
    const [filteredRooms, setFilteredRooms] = useState<Array<any>>([]);

    // State untuk mengelola arah sortasi
    const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');

    // State untuk dialog-dialog
    const [showCreateRoomType, setShowCreateRoomType] = useState<boolean>(false);
    const [showUpdateRoomType, setShowUpdateRoomType] = useState<boolean>(false);
    const [roomIdWillUpdateRoomType, setRoomIdWillUpdateRoomType] = useState<string>('');
    const [showUpdateRoomNumber, setShowUpdateRoomNumber] = useState<boolean>(false);
    const [roomIdWillUpdateRoomNumber, setRoomIdWillUpdateRoomNumber] = useState<string>('');
    const [showCreateRateplan, setShowCreateRateplan] = useState<boolean>(false);
    const [roomIdWillCreateRateplan, setRoomIdWillCreateRateplan] = useState<string>('');
    const [showUpdateRateplan, setShowUpdateRateplan] = useState<boolean>(false);
    const [rateIdWillUpdateRateplan, setRateIdWillUpdateRateplan] = useState<string>('');
    const [showCreateLinkRate, setShowCreateLinkRate] = useState<boolean>(false);
    const [rateIdWillCreateLinkRate, setRateIdWillCreateLinkRate] = useState<string>('');
    const [showUpdateLinkRate, setShowUpdateLinkRate] = useState<boolean>(false);
    const [rateIdWillUpdateLinkRate, setRateIdWillUpdateLinkRate] = useState<string>('');

    // useQuery dimodifikasi untuk bergantung pada sortDirection
    const rooms = useQuery({
        queryKey: ['rooms', 'rates', sortDirection], // queryKey dinamis
        queryFn: () => privateApi.get('/v4/room-type', {
            params: {
                sort: sortDirection, // Gunakan state untuk parameter API
                all: 1
            },
        }).then((res) => res.data.data)
    });

    useEffect(() => {
        if (rooms.data) {
            // Logika filter berjalan setelah data dari API diterima
            if (searchQuery) {
                setFilteredRooms(rooms.data.filter((room: { name: string }) => room.name.toLowerCase().includes(searchQuery.toLowerCase())));
            } else {
                setFilteredRooms(rooms.data);
            }
        }
    }, [rooms.data, searchQuery]); // Bergantung pada data API dan query pencarian

    if (rooms.isError) {
        toast.error('Something went wrong, Failed to fetch rooms & rates');
    }

    const handleSearch = (query: string) => {
        setSearchQuery(query);
    };

    const queryClient = useQueryClient();

    // Mutation untuk menghapus room
    const { mutate } = useMutation({
        mutationFn: async (roomId: string) => {
            return privateApi.delete(`/v4/room-type/${roomId}`);
        },
        onMutate: async (roomId: string) => {
            const previousRooms = queryClient.getQueryData(['rooms', 'rates', sortDirection]);
            queryClient.setQueryData(['rooms', 'rates', sortDirection], (oldRooms: any) => {
                return oldRooms.filter((room: { id: string }) => room.id !== roomId);
            });
            return { previousRooms };
        },
        onSuccess: async () => {
            await queryClient.invalidateQueries({ queryKey: ['rooms', 'rates'] }); // Invalidate semua query rooms
            toast.success('Room type deleted successfully', { id: 'delete-room-type' });

            Swal.fire({
                title: 'Reminder',
                html: 'Room type deleted. <strong>Make sure to re-map it in all channels</strong> so the OTA mapping doesn\'t get left behind.',
                icon: 'info',
                confirmButtonText: 'Got it',
            });
        },
        onError: (error: unknown, _variables: string, context?: { previousRooms?: any }) => {
            if (context?.previousRooms) {
                queryClient.setQueryData(['rooms', 'rates', sortDirection], context.previousRooms);
            }
            let toastMessage = 'Failed to delete room type. Please try again.';
            if (error instanceof AxiosError && error.response?.data?.message) {
                toastMessage = error.response.data.message;
            }
            toast.error(toastMessage, { id: 'delete-room-type' });
        },
    });

    const handleRemoveRoom = (roomId: string) => {
        Swal.fire({
            title: 'Are you sure?',
            html: 'You will not be able to recover this room type!<br /><br /><strong>Check the Channex mapping</strong> for this room type before deleting — it may still be linked to OTA channels.',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Yes, delete it!',
            confirmButtonColor: '#d33',
            cancelButtonColor: '#3085d6',
            cancelButtonText: 'Cancel',
        }).then((result) => {
            if (result.isConfirmed) {
                toast.loading('Deleting room type...', { id: 'delete-room-type' });
                mutate(roomId);
            }
        });
    };

    // Handlers untuk membuka/menutup dialog
    const handleOpenUpdateRoomType = (roomId: string) => { setRoomIdWillUpdateRoomType(roomId); setShowUpdateRoomType(true); };
    const handleCloseUpdateRoomType = () => { setShowUpdateRoomType(false); setRoomIdWillUpdateRoomType(''); };
    const handleOpenUpdateRoomNumber = (roomId: string) => { setRoomIdWillUpdateRoomNumber(roomId); setShowUpdateRoomNumber(true); };
    const handleCloseUpdateRoomNumber = () => { setShowUpdateRoomNumber(false); setRoomIdWillUpdateRoomNumber(''); };
    const handleOpenCreateRateplan = (roomId: string) => { setRoomIdWillCreateRateplan(roomId); setShowCreateRateplan(true); };
    const handleCloseCreateRateplan = () => { setShowCreateRateplan(false); setRoomIdWillCreateRateplan(''); };
    const handleOpenUpdateRateplan = (rateId: string) => { setRateIdWillUpdateRateplan(rateId); setShowUpdateRateplan(true); };
    const handleCloseUpdateRateplan = () => { setShowUpdateRateplan(false); setRateIdWillUpdateRateplan(''); };
    const handleOpenCreateLinkRate = (rateId: string) => { setRateIdWillCreateLinkRate(rateId); setShowCreateLinkRate(true); };
    const handleCloseCreateLinkRate = () => { setShowCreateLinkRate(false); setRateIdWillCreateLinkRate(''); };
    const handleOpenUpdateLinkRate = (rateId: string) => { setRateIdWillUpdateLinkRate(rateId); setShowUpdateLinkRate(true); };
    const handleCloseUpdateLinkRate = () => { setShowUpdateLinkRate(false); setRateIdWillUpdateLinkRate(''); };

    return (
        <AdminLayout>
            <div className="container-fluid mt-5">
                <PropertyInactiveBanner variant="partial" partialNote="Delete room, remove rate plan, and remove link rate remain available." />
                <div className="table-container min-vh-100">
                    {rooms.isLoading &&
                        (
                            <div className="spinner-overlay table-spinner-overlay active">
                                <div className="spinner-border table-spinner-border text-primary" role="status">
                                    <span className="visually-hidden">Loading...</span>
                                </div>
                            </div>
                        )
                    }
                    {/* Header: Search, Sort, and Create */}
                    <div className="search-container mb-3">
                        <div className="input-icon">
                            <input type="text" className="form-control" placeholder="Search room type..." value={searchQuery} onChange={(e) => handleSearch(e.target.value)} />
                            <span className="input-icon-addon">
                                <svg xmlns="http://www.w3.org/2000/svg" className="icon icon-tabler icon-tabler-search" width="24" height="24" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round">
                                    <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                                    <circle cx="10" cy="10" r="7" />
                                    <line x1="21" y1="21" x2="15" y2="15" />
                                </svg>
                            </span>
                        </div>

                        <div className="d-flex align-items-center gap-2">
                            {/* Tombol Sort */}
                            <div className="btn-group">
                                <button
                                    className={clsx("btn btn-outline-primary", sortDirection === 'asc' && 'active')}
                                    onClick={() => setSortDirection('asc')}
                                    title="Sort Ascending (A-Z)"
                                    disabled={rooms.isFetching}
                                >
                                    <IconSortAscendingLetters size={20} />
                                </button>
                                <button
                                    className={clsx("btn btn-outline-primary", sortDirection === 'desc' && 'active')}
                                    onClick={() => setSortDirection('desc')}
                                    title="Sort Descending (Z-A)"
                                    disabled={rooms.isFetching}
                                >
                                    <IconSortDescendingLetters size={20} />
                                </button>
                            </div>

                            {/* Tombol Create */}
                            <RequireAuth requiredRoles={['dev']} requiredPermissions={['room_rate_create']} asComponent>
                                <ReadOnlyGuard>
                                    <button className="btn btn-primary" onClick={() => setShowCreateRoomType(true)}>Create</button>
                                </ReadOnlyGuard>
                            </RequireAuth>
                        </div>
                    </div>

                    {/* Room Table */}
                    <div className="position-relative min-vh-100 table-responsive">
                        {rooms.isSuccess && filteredRooms.length > 0 && <DropdownRooms
                            rooms={filteredRooms}
                            onUpdateRoom={handleOpenUpdateRoomType}
                            onUpdateRoomNumber={handleOpenUpdateRoomNumber}
                            onRemoveRoom={handleRemoveRoom}
                            onCreateRate={handleOpenCreateRateplan}
                            onUpdateRate={handleOpenUpdateRateplan}
                            onCreateLinkRate={handleOpenCreateLinkRate}
                            onUpdateLinkRate={handleOpenUpdateLinkRate}
                        />}
                        {rooms.isSuccess && filteredRooms.length === 0 && (
                            <div className="min-vh-100">
                                <table className="table table-hover table-vcenter">
                                    <thead>
                                        <tr>
                                            <th>Title</th>
                                            <th>Count of Rooms</th>
                                            <th>Occupancy</th>
                                            <th className="text-end">Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr>
                                            <td colSpan={4} className="text-center">
                                                No data available
                                            </td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>

                    <Footer />
                </div>
            </div>
            {/* Dialogs */}
            <RequireAuth requiredRoles={['dev']} requiredPermissions={['room_rate_create']} asComponent>
                <ReadOnlyGuard mode="hide">
                    <CreateRoomTypeDialog show={showCreateRoomType} onClose={() => setShowCreateRoomType(false)} />
                </ReadOnlyGuard>
            </RequireAuth>
            <RequireAuth requiredRoles={['dev']} requiredPermissions={['room_rate_update']} asComponent>
                <ReadOnlyGuard mode="hide">
                    <UpdateRoomTypeDIalog show={showUpdateRoomType} onClose={handleCloseUpdateRoomType} roomTypeId={roomIdWillUpdateRoomType} />
                </ReadOnlyGuard>
            </RequireAuth>
            <RequireAuth requiredRoles={['dev']} requiredPermissions={['room_rate_update']} asComponent>
                <ReadOnlyGuard mode="hide">
                    <UpdateRoomNumberDialog show={showUpdateRoomNumber} onClose={handleCloseUpdateRoomNumber} roomNumberId={roomIdWillUpdateRoomNumber} />
                </ReadOnlyGuard>
            </RequireAuth>
            <RequireAuth requiredRoles={['dev']} requiredPermissions={['room_rate_create']} asComponent>
                <ReadOnlyGuard mode="hide">
                    <CreateRateDialog show={showCreateRateplan} onClose={handleCloseCreateRateplan} roomId={roomIdWillCreateRateplan} />
                </ReadOnlyGuard>
            </RequireAuth>
            <RequireAuth requiredRoles={['dev']} requiredPermissions={['room_rate_update']} asComponent>
                <ReadOnlyGuard mode="hide">
                    <UpdateRateDialog show={showUpdateRateplan} onClose={handleCloseUpdateRateplan} rateId={rateIdWillUpdateRateplan} />
                </ReadOnlyGuard>
            </RequireAuth>
            <RequireAuth requiredRoles={['dev']} requiredPermissions={['room_rate_create']} asComponent>
                <ReadOnlyGuard mode="hide">
                    <CreateLinkRateDialog show={showCreateLinkRate} onClose={handleCloseCreateLinkRate} rateId={rateIdWillCreateLinkRate} />
                </ReadOnlyGuard>
            </RequireAuth>
            <RequireAuth requiredRoles={['dev']} requiredPermissions={['room_rate_update']} asComponent>
                <ReadOnlyGuard mode="hide">
                    <UpdateLinkRateDialog show={showUpdateLinkRate} onClose={handleCloseUpdateLinkRate} rateId={rateIdWillUpdateLinkRate} />
                </ReadOnlyGuard>
            </RequireAuth>
        </AdminLayout>
    );
};

export default RoomRatesSettingPage;

interface Room {
    id: string;
    name: string;
    total_rooms: number;
    occ_adults: number;
    occ_children: number;
}

interface DropdownRoomsProps {
    rooms: Room[];
    onUpdateRoom: (roomId: string) => void;
    onUpdateRoomNumber: (roomId: string) => void;
    onRemoveRoom: (roomId: string) => void;
    onCreateRate: (roomId: string) => void;
    onUpdateRate: (rateId: string) => void;
    onCreateLinkRate: (rateId: string) => void;
    onUpdateLinkRate: (rateId: string) => void;
}

interface SortableRowProps {
    room: Room;
    childRatesVisible: boolean;
    onToggleChildRates: (roomId: string) => void;
    onUpdateRoom: (roomId: string) => void;
    onUpdateRoomNumber: (roomId: string) => void;
    onRemoveRoom: (roomId: string) => void;
    onCreateRate: (roomId: string) => void;
    onUpdateRate: (rateId: string) => void;
    onCreateLinkRate: (rateId: string) => void;
    onUpdateLinkRate: (rateId: string) => void;
}

const SortableRow: React.FC<SortableRowProps> = ({
    room,
    childRatesVisible,
    onToggleChildRates,
    onUpdateRoom,
    onUpdateRoomNumber,
    onRemoveRoom,
    onCreateRate,
    onUpdateRate,
    onCreateLinkRate,
    onUpdateLinkRate
}) => {
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: room.id });

    const style: React.CSSProperties = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.6 : 1,
        backgroundColor: isDragging ? 'var(--tblr-bg-surface-secondary, #f6f8fb)' : undefined,
        zIndex: isDragging ? 9999 : undefined,
        position: isDragging ? 'relative' : undefined,
    };

    return (
        <React.Fragment>
            <tr ref={setNodeRef} style={style}>
                <td style={{ width: '40px', verticalAlign: 'middle', textAlign: 'center' }} {...attributes} {...listeners}>
                    <IconGripVertical className="text-muted" style={{ cursor: 'grab' }} size={18} />
                </td>
                <td style={{ verticalAlign: 'middle' }}>
                    <button
                        className="btn btn-sm btn-link me-2"
                        onClick={() => onToggleChildRates(room.id)}
                    >
                        {childRatesVisible ? '−' : '+'}
                    </button>
                    {room.name}
                </td>
                <td style={{ verticalAlign: 'middle' }}>{room.total_rooms}</td>
                <td style={{ verticalAlign: 'middle' }}>
                    A: {room.occ_adults} C: {room.occ_children}
                </td>
                <td className="text-end" style={{ verticalAlign: 'middle' }}>
                    <Dropdown>
                        <Dropdown.Toggle variant="default" id={`action-dropdown-${room.id}`}>
                            Actions
                        </Dropdown.Toggle>
                        <Dropdown.Menu popperConfig={{ strategy: 'fixed' }}>
                            <RequireAuth requiredRoles={['dev']} requiredPermissions={['room_rate_update']} asComponent>
                                <ReadOnlyGuard mode="hide">
                                    <Dropdown.Item href='#' onClick={(e) => { e.preventDefault(); onUpdateRoom(room.id); }}>Edit Room Type</Dropdown.Item>
                                </ReadOnlyGuard>
                            </RequireAuth>
                            <RequireAuth requiredRoles={['dev']} requiredPermissions={['room_rate_update']} asComponent>
                                <ReadOnlyGuard mode="hide">
                                    <Dropdown.Item href='#' onClick={(e) => { e.preventDefault(); onUpdateRoomNumber(room.id); }}>Edit Room Numbers</Dropdown.Item>
                                </ReadOnlyGuard>
                            </RequireAuth>
                            <RequireAuth requiredRoles={['dev']} requiredPermissions={['room_rate_create']} asComponent>
                                <ReadOnlyGuard mode="hide">
                                    <Dropdown.Item href='#' onClick={(e) => { e.preventDefault(); onCreateRate(room.id); }}>Add Rate</Dropdown.Item>
                                </ReadOnlyGuard>
                            </RequireAuth>
                            {/* Delete stays enabled while the property is inactive — deliberate carve-out, see IndexAi/property-management.md */}
                            <RequireAuth requiredRoles={['dev']} requiredPermissions={['room_rate_delete']} asComponent>
                                <Dropdown.Item href='#' onClick={(e) => { e.preventDefault(); onRemoveRoom(room.id); }}>
                                    <span className="text-danger">Delete Room</span>
                                </Dropdown.Item>
                            </RequireAuth>
                        </Dropdown.Menu>
                    </Dropdown>
                </td>
            </tr>
            {childRatesVisible && (
                <Rates room_id={room.id} onUpdateRate={onUpdateRate} onCreateLinkRate={onCreateLinkRate} onUpdateLinkRate={onUpdateLinkRate} />
            )}
        </React.Fragment>
    );
};

const DropdownRooms: React.FC<DropdownRoomsProps> = ({ rooms, onUpdateRoom, onUpdateRoomNumber, onRemoveRoom, onCreateRate, onUpdateRate, onCreateLinkRate, onUpdateLinkRate }) => {
    const [childRatesVisible, setChildRatesVisible] = useState<{ [key: string]: boolean }>({});
    const [sortedRooms, setSortedRooms] = useState<Room[]>(rooms);

    useEffect(() => {
        setSortedRooms(rooms);
    }, [rooms]);

    const { mutate: sortRoomTypes } = useSortRoomTypes();
    const { isReadOnly } = usePropertyReadOnly();

    const sensors = useSensors(
        useSensor(PointerSensor, {
            activationConstraint: {
                distance: 8,
            },
        }),
        useSensor(KeyboardSensor, {
            coordinateGetter: sortableKeyboardCoordinates,
        })
    );

    const handleToggleChildRates = (roomId: string) => {
        setChildRatesVisible((prev) => ({ ...prev, [roomId]: !prev[roomId] }));
    };

    const handleDragEnd = (event: DragEndEvent) => {
        if (isReadOnly) return;

        const { active, over } = event;

        if (over && active.id !== over.id) {
            const oldIndex = sortedRooms.findIndex((room) => room.id === active.id);
            const newIndex = sortedRooms.findIndex((room) => room.id === over.id);

            const newSorted = arrayMove(sortedRooms, oldIndex, newIndex);
            setSortedRooms(newSorted);

            const roomTypeIds = newSorted.map((room) => room.id);
            sortRoomTypes(roomTypeIds);
        }
    };

    return (
        <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
        >
            <table className="table table-hover table-vcenter">
                <thead>
                    <tr>
                        <th style={{ width: '40px' }}></th>
                        <th>Title</th>
                        <th>Count of Rooms</th>
                        <th>Occupancy</th>
                        <th className="text-end">Actions</th>
                    </tr>
                </thead>
                <tbody>
                    <SortableContext
                        items={sortedRooms.map((room) => room.id)}
                        strategy={verticalListSortingStrategy}
                    >
                        {sortedRooms.map((room) => (
                            <SortableRow
                                key={room.id}
                                room={room}
                                childRatesVisible={!!childRatesVisible[room.id]}
                                onToggleChildRates={handleToggleChildRates}
                                onUpdateRoom={onUpdateRoom}
                                onUpdateRoomNumber={onUpdateRoomNumber}
                                onRemoveRoom={onRemoveRoom}
                                onCreateRate={onCreateRate}
                                onUpdateRate={onUpdateRate}
                                onCreateLinkRate={onCreateLinkRate}
                                onUpdateLinkRate={onUpdateLinkRate}
                            />
                        ))}
                    </SortableContext>
                </tbody>
            </table>
        </DndContext>
    );
};